import type {
  ListActiveDestinationsRequest,
  ListActiveDestinationsResponse,
  ListAllDestinationsForEnvResponse,
} from "@/api/app";
import type {
  DestinationMetricsQuery,
  DestinationMetricsQueryGroupBy,
  DestinationMetricsQueryResult,
} from "@/admin/destinationMetricsTypes";
import type {
  ImpressionMetricsQuery,
  ImpressionMetricsQueryResult,
} from "@/admin/impressionMetricsTypes";
import type { LoadedDestinationInfo } from "@/types/global";

import { getPreviewGraph } from "../graph";
import {
  generateImpressionRows,
  generateOutcomeRows,
  type DestOutcomeRow,
  type ImpressionRow,
  type TargetDestination,
} from "../graph/destination-metrics";
import type { PreviewDestination } from "../graph/types";
import { PREVIEW_ENV_ID } from "../runtime";

// d.key is a real catalog key, but LoadedDestinationInfo["name"] is the narrow
// DestinationKey union; the cast bridges string -> union. type_slug is
// recomputed downstream via slugForDestination(name), so the value set here is
// a placeholder that gets overwritten.
export function toLoadedDestinationInfo(
  d: PreviewDestination,
): LoadedDestinationInfo {
  return {
    id: d.id,
    name: d.key as unknown as LoadedDestinationInfo["name"],
    instance_name: d.name,
    custom_label: d.name,
    type_slug: d.key,
    enabled: d.isEnabled,
    env_id: PREVIEW_ENV_ID,
    is_valid: true,
    is_valid_client_side: true,
    is_valid_server_side: true,
    fivetran_group_id: null,
    has_baa: false,
    connection_mode: "server",
    connected_audiences_count: 0,
    destinationCategories: [],
    settings: d.settings,
  };
}

export function handleListActiveDestinations(
  _params: ListActiveDestinationsRequest,
): ListActiveDestinationsResponse {
  const graph = getPreviewGraph();
  return {
    destinations: graph.destinations.map(toLoadedDestinationInfo),
  };
}

// The destinations pages list via list_all_destinations_for_env (not
// list_active_destinations). Without intercepting it the pages render the real
// account's destinations, whose IDs never match our synthetic metrics — so the
// overview joins to nothing. Serve the same synthetic set here so the overview
// list and its metrics share IDs.
export function handleListAllDestinationsForEnv(
  _params: Record<string, never>,
): ListAllDestinationsForEnvResponse {
  const graph = getPreviewGraph();
  return {
    destinations: graph.destinations.map(toLoadedDestinationInfo),
  };
}

// Resolves which destinations a metrics query targets: the explicit IDs from a
// detail-page filter (works for any ID, real or synthetic), or the full
// synthetic set for the overview, which filters by account/env instead.
function targetDestinations(filterIDs?: string[]): TargetDestination[] {
  const graph = getPreviewGraph();
  const toTarget = (
    d: (typeof graph.destinations)[number],
  ): TargetDestination => ({
    id: d.id,
    type: d.key,
    hasDeliveryMetrics: d.hasDeliveryMetrics,
    hasImpressions: d.hasImpressions,
  });
  const byID = new Map(graph.destinations.map((d) => [d.id, toTarget(d)]));
  const ids = (filterIDs ?? []).filter(Boolean);
  if (ids.length > 0) {
    // Unknown (real) IDs default to delivery metrics but no impressions.
    return ids.map(
      (id) =>
        byID.get(id) ?? {
          id,
          type: "",
          hasDeliveryMetrics: true,
          hasImpressions: false,
        },
    );
  }
  return graph.destinations.map(toTarget);
}

function eventDefIDs(): string[] {
  return getPreviewGraph().eventDefs.map((d) => d.eventDefId);
}

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
type Interval = "hour" | "day" | "week" | "month";

function floorToInterval(ms: number, interval: Interval): number {
  const d = new Date(ms);
  switch (interval) {
    case "hour":
      d.setUTCMinutes(0, 0, 0);
      return d.getTime();
    case "week": {
      d.setUTCHours(0, 0, 0, 0);
      d.setUTCDate(d.getUTCDate() - d.getUTCDay());
      return d.getTime();
    }
    case "month":
      return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
    case "day":
    default:
      d.setUTCHours(0, 0, 0, 0);
      return d.getTime();
  }
}

function nextBucket(ms: number, interval: Interval): number {
  const d = new Date(ms);
  switch (interval) {
    case "hour":
      return ms + HOUR_MS;
    case "week":
      return ms + 7 * DAY_MS;
    case "month":
      return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
    case "day":
    default:
      return ms + DAY_MS;
  }
}

function buildBuckets(from: number, to: number, interval: Interval): number[] {
  const buckets: number[] = [];
  const last = floorToInterval(to, interval);
  for (
    let t = floorToInterval(from, interval);
    t <= last;
    t = nextBucket(t, interval)
  ) {
    buckets.push(t);
  }
  return buckets;
}

// One place that decides bucketing for every metrics query, so the time axis,
// the series tag, and the row->bucket mapping can't drift between consumers.
// An undefined interval means "one aggregated bucket over the whole range" —
// matching the backend, which is what headline cards read as deliveredCount[0].
interface BucketPlan {
  buckets: number[];
  indexOf: (time: number) => number; // -1 when the row falls outside the range
}

function bucketPlan(
  from: number,
  to: number,
  interval: Interval | undefined,
): BucketPlan {
  if (!interval) {
    return {
      buckets: [from],
      indexOf: (t) => (t >= from && t <= to ? 0 : -1),
    };
  }
  const buckets = buildBuckets(from, to, interval);
  const index = new Map(buckets.map((b, i) => [b, i] as const));
  return {
    buckets,
    indexOf: (t) => index.get(floorToInterval(t, interval)) ?? -1,
  };
}

const rawInterval = (ts: { interval?: unknown }): Interval | undefined =>
  ts.interval as Interval | undefined;

type Series = DestinationMetricsQueryResult["series"];

// Runs `run` for the current window, plus the previous window when compare is
// set, concatenating the results. The previous-window math lives here only, so
// the two metrics handlers can't drift on how the comparison range is derived.
function withComparison<T>(
  ts: { dateFrom: Date; dateTo: Date; compare: boolean; interval?: unknown },
  run: (
    from: number,
    to: number,
    plan: BucketPlan,
    series: Series,
    salt: string,
  ) => T[],
): T[] {
  const interval = rawInterval(ts);
  const from = new Date(ts.dateFrom).getTime();
  const to = new Date(ts.dateTo).getTime();
  const current = run(from, to, bucketPlan(from, to, interval), "current", "");
  if (!ts.compare) return current;
  const prevTo = from - 1;
  const prevFrom = prevTo - (to - from);
  return current.concat(
    run(
      prevFrom,
      prevTo,
      bucketPlan(prevFrom, prevTo, interval),
      "previous",
      "prev",
    ),
  );
}

// Aggregates pre-generated outcome rows into the wide, time-bucketed result
// shape the destinations pages expect. Generic over groupBy/include so a single
// implementation covers the overview table, the overview charts, and the detail
// page — and overview/detail reconcile because they sum the same rows.
function aggregateOutcomeRows(
  rows: DestOutcomeRow[],
  query: DestinationMetricsQuery,
  plan: BucketPlan,
  series: DestinationMetricsQueryResult["series"],
): DestinationMetricsQueryResult[] {
  const graph = getPreviewGraph();
  const eventNames = new Map(
    graph.eventDefs.map((d) => [d.eventDefId, d.name] as const),
  );
  const buckets = plan.buckets;
  const groupBy = query.groupBy ?? [];
  const has = (g: DestinationMetricsQueryGroupBy) => groupBy.includes(g);

  type Acc = {
    destinationID: string;
    destinationType: string;
    eventDefID: string;
    outcome: DestOutcomeRow["outcome"];
    total: number[];
    perOutcome: Record<DestOutcomeRow["outcome"], number[]>;
    latWeighted: number[];
    latCount: number[];
    latMax: number[];
  };

  const zeros = () => new Array(buckets.length).fill(0);
  const groups = new Map<string, Acc>();

  for (const row of rows) {
    const idx = plan.indexOf(row.time);
    if (idx < 0) continue;

    const key = groupBy
      .map((g) => String(row[g as keyof DestOutcomeRow]))
      .join("|");
    let acc = groups.get(key);
    if (!acc) {
      acc = {
        destinationID: row.destinationID,
        destinationType: row.destinationType,
        eventDefID: row.eventDefID,
        outcome: row.outcome,
        total: zeros(),
        perOutcome: {
          delivered: zeros(),
          failed: zeros(),
          dropped: zeros(),
          delayed: zeros(),
        },
        latWeighted: zeros(),
        latCount: zeros(),
        latMax: zeros(),
      };
      groups.set(key, acc);
    }
    acc.total[idx] += row.count;
    acc.perOutcome[row.outcome][idx] += row.count;
    acc.latWeighted[idx] += row.avgLatencyMS * row.count;
    acc.latCount[idx] += row.count;
    acc.latMax[idx] = Math.max(acc.latMax[idx], row.maxLatencyMS);
  }

  const rate = (num: number[], den: number[]) =>
    num.map((n, i) => (den[i] > 0 ? n / den[i] : 0));

  return Array.from(groups.values()).map((acc) => ({
    accountID: null,
    destinationType:
      has("destinationID") || has("destinationType")
        ? acc.destinationType
        : null,
    envID: has("envID") ? PREVIEW_ENV_ID : null,
    destinationID: has("destinationID") ? acc.destinationID : null,
    eventDefID: has("eventDefID") ? acc.eventDefID : null,
    eventName: has("eventDefID")
      ? (eventNames.get(acc.eventDefID) ?? null)
      : null,
    outcome: has("outcome") ? acc.outcome : null,
    outcomeReason: has("outcomeReason")
      ? acc.outcome === "delivered"
        ? "delivered"
        : null
      : null,
    // All synthetic events are active so the Status column reads "Enabled".
    eventEnabled: true,
    series,
    timeBuckets: buckets.map((b) => new Date(b)),
    count: acc.total,
    deliveredCount: acc.perOutcome.delivered,
    failedCount: acc.perOutcome.failed,
    droppedCount: acc.perOutcome.dropped,
    delayedCount: acc.perOutcome.delayed,
    deliveredRate: rate(acc.perOutcome.delivered, acc.total),
    failedRate: rate(acc.perOutcome.failed, acc.total),
    dropRate: rate(acc.perOutcome.dropped, acc.total),
    delayedRate: rate(acc.perOutcome.delayed, acc.total),
    avgLatencyMS: acc.latWeighted.map((w, i) =>
      acc.latCount[i] > 0 ? Math.round(w / acc.latCount[i]) : 0,
    ),
    maxLatencyMS: acc.latMax,
  }));
}

// Generation already scopes rows to the target destinations and window, so only
// the event-def / outcome / type filters are load-bearing here.
function filterOutcomeRows(
  rows: DestOutcomeRow[],
  query: DestinationMetricsQuery,
): DestOutcomeRow[] {
  const f = query.filter ?? {};
  const eventDefIDs =
    f.eventDefIDs ?? (f.eventDefID ? [f.eventDefID] : undefined);
  return rows.filter((r) => {
    if (f.destinationType && r.destinationType !== f.destinationType)
      return false;
    if (eventDefIDs && !eventDefIDs.includes(r.eventDefID)) return false;
    if (f.outcomes && !f.outcomes.includes(r.outcome)) return false;
    return true;
  });
}

export function handleQueryDestinationMetrics(
  query: DestinationMetricsQuery,
): DestinationMetricsQueryResult[] {
  const destinations = targetDestinations(query.filter?.destinationIDs);
  const defIDs = eventDefIDs();
  return withComparison(query.timeseries, (from, to, plan, series, salt) =>
    aggregateOutcomeRows(
      filterOutcomeRows(
        generateOutcomeRows({
          destinations,
          eventDefIDs: defIDs,
          windowStart: from,
          windowEnd: to,
          salt,
        }),
        query,
      ),
      query,
      plan,
      series,
    ),
  );
}

function aggregateImpressionRows(
  rows: ImpressionRow[],
  query: ImpressionMetricsQuery,
  plan: BucketPlan,
  series: ImpressionMetricsQueryResult["series"],
): ImpressionMetricsQueryResult[] {
  const buckets = plan.buckets;
  const groupBy = query.groupBy ?? [];
  const has = (g: string) => groupBy.includes(g as never);

  type Acc = {
    destinationID: string;
    destinationType: string;
    count: number[];
  };
  const groups = new Map<string, Acc>();

  for (const row of rows) {
    const idx = plan.indexOf(row.time);
    if (idx < 0) continue;
    const key = groupBy
      .map((g) => String(row[g as keyof ImpressionRow]))
      .join("|");
    let acc = groups.get(key);
    if (!acc) {
      acc = {
        destinationID: row.destinationID,
        destinationType: row.destinationType,
        count: new Array(buckets.length).fill(0),
      };
      groups.set(key, acc);
    }
    acc.count[idx] += row.count;
  }

  return Array.from(groups.values()).map((acc) => ({
    destinationType: (has("destinationID") || has("destinationType")
      ? acc.destinationType
      : "") as ImpressionMetricsQueryResult["destinationType"],
    accountID: "" as ImpressionMetricsQueryResult["accountID"],
    envID: (has("envID")
      ? PREVIEW_ENV_ID
      : "") as ImpressionMetricsQueryResult["envID"],
    destinationID: has("destinationID") ? acc.destinationID : "",
    count: acc.count,
    timeBuckets: buckets.map((b) => new Date(b)),
    series,
  }));
}

export function handleQueryImpressionMetrics(
  query: ImpressionMetricsQuery,
): ImpressionMetricsQueryResult[] {
  const f = query.filter ?? {};
  const destinations = targetDestinations(
    f.destinationID ? [f.destinationID] : undefined,
  ).filter((d) => !f.destinationType || d.type === f.destinationType);
  return withComparison(query.timeseries, (from, to, plan, series, salt) =>
    aggregateImpressionRows(
      generateImpressionRows({
        destinations,
        windowStart: from,
        windowEnd: to,
        salt,
      }),
      query,
      plan,
      series,
    ),
  );
}
